import { useCallback, useEffect, useState } from 'react';
import { Badge, Field, Note, Panel, ShieldIcon, Spinner } from '@wyocspay/ui';
import { ApiError, api, type CredentialStatus } from './api.js';

/**
 * Stripe credentials.
 *
 * Entered here rather than by editing a file on the server, so rotating a key
 * does not require shell access to the machine that holds the money.
 *
 * Nothing that is typed here is ever displayed back. The server returns a
 * prefix and a length so it is possible to confirm which key is in place, and
 * that is all - a console that could show a secret key is a console that leaks
 * one to whoever is standing behind you.
 */

const LABELS: Record<string, { label: string; help: string; where: string }> = {
  STRIPE_SECRET_KEY: {
    label: 'Таен ключ',
    help: 'Започва с sk_test_ или sk_live_. С него платформата говори със Stripe от името на вашия профил.',
    where: 'Developers → API keys',
  },
  STRIPE_PUBLISHABLE_KEY: {
    label: 'Публичен ключ',
    help: 'Започва с pk_test_ или pk_live_. Стои в браузъра на клиента и е предназначен да е публичен.',
    where: 'Developers → API keys',
  },
  STRIPE_WEBHOOK_SECRET: {
    label: 'Подпис за плащания',
    help: 'Започва с whsec_. С него се проверява, че съобщението наистина идва от Stripe.',
    where: 'Developers → Webhooks → вашият endpoint',
  },
  STRIPE_CONNECT_WEBHOOK_SECRET: {
    label: 'Подпис за свързани профили',
    help: 'Отделен whsec_ за втория endpoint, този за свързаните профили.',
    where: 'Developers → Webhooks → endpoint за Connected accounts',
  },
};

const ORDER = [
  'STRIPE_SECRET_KEY',
  'STRIPE_PUBLISHABLE_KEY',
  'STRIPE_WEBHOOK_SECRET',
  'STRIPE_CONNECT_WEBHOOK_SECRET',
] as const;

export function CredentialsView() {
  const [status, setStatus] = useState<CredentialStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await api.credentials());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const filled = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v.trim().length > 0),
      );
      const result = await api.saveCredentials(filled);
      setValues({});
      setSaved(
        result.account
          ? `Записано и проверено срещу Stripe. Профил ${result.account}. Услугата трябва да се рестартира, за да ги ползва.`
          : 'Записано. Услугата трябва да се рестартира, за да ги ползва.',
      );
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (error && !status) return <Note tone="danger">{error}</Note>;
  if (!status) return <Spinner label="Зареждане…" />;

  const anyFilled = Object.values(values).some((v) => v.trim().length > 0);

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 820 }}>
      {!status.configured ? (
        <Note tone="warn" title="Платформата още не приема плащания">
          Липсват: {status.missing.map((m) => LABELS[m]?.label ?? m).join(', ')}. Докато ги няма,
          всяко плащане връща грешка и казва защо - нищо не се преструва, че работи.
        </Note>
      ) : (
        <Note tone={status.livemode ? 'warn' : 'ok'} title={status.livemode ? 'РЕЖИМ НА ЖИВО' : 'Тестов режим'}>
          {status.livemode
            ? 'Плащанията са реални. Картите се таксуват наистина.'
            : 'Нищо не се таксува реално. Ползвайте тестовите карти на Stripe.'}
        </Note>
      )}

      {saved ? <Note tone="ok">{saved}</Note> : null}
      {error ? <Note tone="danger">{error}</Note> : null}

      <Panel title="Адресите, които се въвеждат в Stripe">
        <p style={{ marginTop: 0, fontSize: 13.5, color: 'var(--wc-muted)' }}>
          Създайте два endpoint-а в Stripe с тези адреси. Всеки ще ви даде по един подпис, който се
          въвежда по-долу.
        </p>
        <dl style={{ margin: 0, display: 'grid', gap: 10 }}>
          <Url label="Плащания" value={status.webhook_urls.payments} />
          <Url label="Свързани профили" value={status.webhook_urls.connect} />
        </dl>
      </Panel>

      <Panel title="Ключове">
        <Note tone="warn">
          Каквото въведете тук, не се показва обратно - нито на вас, нито на друг. Вижда се само
          началото и дължината, колкото да се разпознае кой ключ е поставен.
        </Note>

        <div style={{ display: 'grid', gap: 18, marginTop: 16 }}>
          {ORDER.map((key) => {
            const meta = LABELS[key];
            const state = status.fields[key];
            return (
              <div key={key}>
                <Field
                  label={meta.label}
                  hint={
                    <>
                      {meta.help}
                      <br />
                      Намира се в Stripe: <strong>{meta.where}</strong>
                    </>
                  }
                >
                  <input
                    className="wc-input"
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={state.present ? 'въведете нов, за да замените' : 'не е зададен'}
                    value={values[key] ?? ''}
                    onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  />
                </Field>
                <div style={{ marginTop: -6, fontSize: 12.5 }}>
                  {state.present ? (
                    <>
                      <Badge tone="ok" icon={false}>ЗАДАДЕН</Badge>{' '}
                      <code className="wc-num" style={{ color: 'var(--wc-faint)' }}>
                        {state.hint}
                      </code>{' '}
                      <span style={{ color: 'var(--wc-faint)' }}>· {state.length} знака</span>
                    </>
                  ) : (
                    <Badge tone="fail" icon={false}>ЛИПСВА</Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="wc-btn"
          onClick={submit}
          disabled={busy || !anyFilled}
          style={{ marginTop: 18 }}
        >
          <ShieldIcon size={13} /> {busy ? 'Проверявам срещу Stripe…' : 'Запази'}
        </button>

        <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--wc-faint)' }}>
          Тайният ключ се проверява срещу Stripe, преди да бъде записан. Ако е сгрешен, нищо не се
          запазва - печатна грешка, открита при първото плащане на клиент, е скъпият начин да се
          разбере.
        </p>
      </Panel>

      <Panel title="След записване">
        <p style={{ margin: 0, fontSize: 13.5 }}>
          Услугата чете конфигурацията си веднъж, при стартиране. За да влязат новите ключове,
          изпълнете на сървъра:
        </p>
        <pre
          className="wc-num"
          style={{
            margin: '10px 0 0',
            padding: '10px 12px',
            background: 'var(--wc-ink)',
            color: '#e8f0f6',
            fontSize: 12.5,
            userSelect: 'all',
          }}
        >
          sudo systemctl restart wyocspay-api
        </pre>
        <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--wc-muted)' }}>
          Презареждане в движение би значело всеки модул, който държи връзка към Stripe, да забележи
          промяната - а един, който я пропусне, ще продължи тихо със стария ключ. Рестартът е
          секунда престой и никаква двусмисленост.
        </p>
      </Panel>
    </div>
  );
}

function Url({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
      <dt style={{ fontSize: 13, color: 'var(--wc-muted)', minWidth: 150 }}>{label}</dt>
      <dd style={{ margin: 0, flex: 1, minWidth: 0 }}>
        <code
          className="wc-num"
          style={{ fontSize: 12.5, userSelect: 'all', wordBreak: 'break-all' }}
        >
          {value}
        </code>
      </dd>
      <button
        type="button"
        className="wc-btn wc-btn-sm wc-btn-secondary"
        onClick={() => {
          void navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          });
        }}
      >
        {copied ? 'копирано' : 'копирай'}
      </button>
    </div>
  );
}
